import { selectManualLanguage } from './hotspeed-manual-utils.js';

function setState(root, state) {
  root.dataset.state = state;
  root.querySelector('[data-pdf-stage]').setAttribute('aria-busy', String(state === 'loading'));
  root.querySelector('[data-pdf-error]').hidden = state !== 'error';
}

async function initPdfViewer(root) {
  if (root.dataset.pdfViewerReady === 'true') return;
  root.dataset.pdfViewerReady = 'true';

  const options = JSON.parse(root.querySelector('[data-language-options]').textContent);
  const select = root.querySelector('[data-language-select]');
  const stage = root.querySelector('[data-pdf-stage]');
  const canvas = root.querySelector('[data-pdf-canvas]');
  const context = canvas.getContext('2d');
  const previousButton = root.querySelector('[data-pdf-prev]');
  const nextButton = root.querySelector('[data-pdf-next]');
  const progress = root.querySelector('[data-pdf-progress]');

  options.forEach(({ code, label }) => select.add(new Option(label, code)));
  select.value = selectManualLanguage(
    options.map(({ code }) => code),
    root.dataset.siteLocale,
  );

  let pdfjs;
  let loadingTask;
  let pdf;
  let pageNumber = 1;
  let scale = 1;
  let renderTask;
  let activeLanguage = select.value;
  let loadSequence = 0;

  const current = () => options.find(({ code }) => code === activeLanguage) || options[0];

  function updateControls() {
    previousButton.disabled = !pdf || pageNumber <= 1;
    nextButton.disabled = !pdf || pageNumber >= pdf.numPages;
  }

  async function renderPage() {
    if (!pdf) return;
    if (renderTask) renderTask.cancel();

    const page = await pdf.getPage(pageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    const availableWidth = Math.max(stage.clientWidth - 24, 280);
    const fitScale = availableWidth / baseViewport.width;
    const viewport = page.getViewport({ scale: fitScale * scale });
    const ratio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.setAttribute('aria-label', `Page ${pageNumber} of ${pdf.numPages}`);
    root.querySelector('[data-pdf-page]').textContent = pageNumber;
    updateControls();

    renderTask = page.render({
      canvasContext: context,
      viewport,
      transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0],
    });

    try {
      await renderTask.promise;
    } catch (error) {
      if (error?.name !== 'RenderingCancelledException') throw error;
    }

    if (pageNumber < pdf.numPages) pdf.getPage(pageNumber + 1).catch(() => {});
  }

  async function load(language) {
    const sequence = ++loadSequence;
    activeLanguage = language;
    const file = current();
    const download = root.querySelector('[data-pdf-download]');
    const open = root.querySelector('[data-pdf-open]');

    setState(root, 'loading');
    updateControls();
    download.href = file.url;
    download.download = file.filename;
    open.href = file.url;
    progress.removeAttribute('value');

    if (renderTask) renderTask.cancel();
    if (loadingTask) await loadingTask.destroy();
    if (sequence !== loadSequence) return;

    loadingTask = pdfjs.getDocument({
      url: file.url,
      enableScripting: false,
      isEvalSupported: false,
      disableRange: false,
      disableStream: false,
      disableAutoFetch: true,
    });

    loadingTask.onProgress = ({ loaded, total }) => {
      if (sequence !== loadSequence) return;
      if (total > 0) progress.value = Math.round((loaded / total) * 100);
      else progress.removeAttribute('value');
    };

    try {
      const loadedPdf = await loadingTask.promise;
      if (sequence !== loadSequence) {
        await loadedPdf.destroy();
        return;
      }
      pdf = loadedPdf;
      pageNumber = 1;
      scale = 1;
      root.querySelector('[data-pdf-pages]').textContent = pdf.numPages;
      await renderPage();
      setState(root, 'ready');
    } catch (error) {
      if (sequence === loadSequence && error?.name !== 'RenderingCancelledException') {
        pdf = undefined;
        setState(root, 'error');
        updateControls();
      }
    }
  }

  try {
    pdfjs = await import(root.dataset.pdfModuleUrl);
    pdfjs.GlobalWorkerOptions.workerSrc = root.dataset.pdfWorkerUrl;
  } catch {
    const file = current();
    const download = root.querySelector('[data-pdf-download]');
    download.href = file.url;
    download.download = file.filename;
    root.querySelector('[data-pdf-open]').href = file.url;
    setState(root, 'error');
    return;
  }

  select.addEventListener('change', () => load(select.value));
  root.querySelector('[data-pdf-retry]').addEventListener('click', () => load(activeLanguage));
  previousButton.addEventListener('click', () => {
    if (pageNumber > 1) {
      pageNumber -= 1;
      renderPage();
    }
  });
  nextButton.addEventListener('click', () => {
    if (pdf && pageNumber < pdf.numPages) {
      pageNumber += 1;
      renderPage();
    }
  });
  root.querySelector('[data-pdf-zoom-out]').addEventListener('click', () => {
    scale = Math.max(0.75, scale - 0.25);
    renderPage();
  });
  root.querySelector('[data-pdf-zoom-in]').addEventListener('click', () => {
    scale = Math.min(2.5, scale + 0.25);
    renderPage();
  });
  root.querySelector('[data-pdf-fullscreen]').addEventListener('click', () => {
    if (stage.requestFullscreen) {
      stage.requestFullscreen().catch(() => window.open(current().url, '_blank', 'noopener'));
    }
    else window.open(current().url, '_blank', 'noopener');
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (pdf) renderPage();
    }, 150);
  });

  await load(activeLanguage);
}

function initPdfViewers(scope) {
  const roots = [...scope.querySelectorAll('[data-pdf-viewer]')];
  if (scope.matches?.('[data-pdf-viewer]')) roots.unshift(scope);
  roots.forEach((root) => {
    initPdfViewer(root).catch(() => setState(root, 'error'));
  });
}

if (typeof document !== 'undefined') {
  initPdfViewers(document);
  document.addEventListener('shopify:section:load', (event) => {
    initPdfViewers(event.target);
  });
}
